import React, { FC, useEffect, useMemo } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { FilterSelect, MaybeTooltip } from '@modernloop/shared/components';
import { LinearProgress, ListItem, ListItemButton, Tooltip, Typography } from '@mui/material';
import { first, uniqBy } from 'lodash';

import useCalendars from 'src/hooks/useCalendars';
import useDirectoryInfo from 'src/hooks/useDirectoryInfo';

import { Calendar } from 'src/utils/api/getCalendars';

import { PAGE_SIZE } from 'src/constants';

const IS_READ_ONLY = false;

interface CalendarSelectProps {
  calendarId?: string | null | undefined;
  onChange?: (remoteCalendarId: string | null) => void;
  error?: boolean;

  dataTestId?: string;

  // Whether or not to call onChange(null) after loading all calendars and not finding the passed in `calendarId`
  // in the users access list
  resetUnknownCalendarsOnLoad?: boolean;

  // When using a default calendar, set the label
  defaultCalendarLabel?: string;
}

const CalendarSelect: FC<CalendarSelectProps> = ({
  calendarId,
  dataTestId,
  error,
  onChange,
  resetUnknownCalendarsOnLoad = true,
  defaultCalendarLabel,
}) => {
  const { t } = useTranslation();
  const { isLoading, isError, data } = useCalendars(PAGE_SIZE, IS_READ_ONLY);
  const directoryInfo = useDirectoryInfo();

  const hasSelected = !!calendarId;

  const nullOption: Calendar = useMemo(
    () => ({
      id: null,
      summary: t('hiring-dashboard-schedule:schedule.communications.calendar.selectCalendar'),
    }),
    [t]
  );

  const calendars = useMemo(() => {
    const result: Calendar[] = [];

    if (hasSelected) {
      result.push(nullOption);
    }

    if (!data?.pages?.length) {
      return result;
    }

    return uniqBy(result.concat(data.pages.map((p) => p.calendar).flat()), (c) => c.id);
  }, [data?.pages, hasSelected, nullOption]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    if (
      onChange &&
      calendarId &&
      calendars.length &&
      !calendars.find((cal) => cal.id === calendarId) &&
      resetUnknownCalendarsOnLoad
    ) {
      onChange(null);
    }
  }, [calendarId, resetUnknownCalendarsOnLoad, onChange, calendars]);

  if (isLoading || !data) {
    return (
      <LinearProgress
        data-testid="calendar-select-loading"
        style={{ width: '100%', marginTop: 12, marginBottom: 12 }}
      />
    );
  }

  if (isError) {
    return (
      <Typography variant="body2">
        {t('hiring-dashboard-schedule:schedule.communications.calendar.calendarsFailedToLoad', {
          calendarName: directoryInfo.label,
        })}
      </Typography>
    );
  }

  const handleChange = (_event, option: Calendar) => {
    console.log('option', option);
    if (onChange) {
      onChange((option && option.id) || '');
    }
  };

  const getOptionLabel = (option: Calendar) => option.summary;
  const renderOption = (props, option: Calendar) => (
    <ListItem {...props}>
      <ListItemButton>
        <MaybeTooltip title={option.summary} label={option.summary} labelProps={{ variant: 'body2' }} />
      </ListItemButton>
    </ListItem>
  );

  const getOptionFromValue = (value: string | null | undefined) => {
    if (!value) {
      return nullOption;
    }

    const option = first((calendars as Calendar[]).filter((calendarOption) => calendarOption.id === value)) || null;

    if (option) {
      if (defaultCalendarLabel && option.summary.indexOf(`${defaultCalendarLabel}`) === -1) {
        option.summary = `${defaultCalendarLabel} (${option.summary})`;
      }
      return option;
    }
    const unknownOption = {
      id: value,
      summary: t('hiring-dashboard-schedule:schedule.communications.calendar.unknownCalendar'),
    };
    calendars.push(unknownOption);
    return unknownOption;
  };

  const option = getOptionFromValue(calendarId);

  return (
    <Tooltip title={option.summary} placement="top-start">
      <FilterSelect
        single
        data-testid={dataTestId || 'calendar-select'}
        options={calendars}
        getLabel={() => option.summary}
        getOptionLabel={getOptionLabel}
        onChange={(_event, value) => handleChange(_event, value[0])}
        renderOption={renderOption}
        error={error}
      />
    </Tooltip>
  );
};

export default CalendarSelect;
